import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, User, Archive, RotateCcw, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AdminShell } from './admin-shell';
import { cn } from '@/lib/utils';

interface OrgDetailData {
  org: {
    orgId: string; name: string; kind: 'personal' | 'team';
    domain: string | null; githubInstallId: string | null;
    createdBy: string; createdAt: string; updatedAt: string;
    archivedAt: string | null; archivedBy: string | null; archivedReason: string | null;
  };
  members: Array<{
    userId: string; role: string; createdAt: string;
    email: string | null; displayName: string | null;
    suspendedAt: string | null;
  }>;
  portfolios: Array<{
    portfolioId: string; name: string; description: string | null;
    isDefault: boolean | null; createdAt: string;
  }>;
  projects: Array<{
    projectId: string; portfolioId: string; name: string;
    targetUrl: string | null; status: string | null;
    healthScore: number | null; lastScanAt: string | null;
    findingCounts: Record<string, number>;
  }>;
  runStats: Record<string, number>;
  findingStats: Record<string, number>;
  openInvites: Array<{ inviteId: string; email: string; role: string; expiresAt: string }>;
}

export function AdminOrgDetailPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState<'archive' | 'unarchive' | null>(null);
  const [reason, setReason] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'org', orgId],
    queryFn: () => apiClient.adminGetOrg(orgId!),
    enabled: !!orgId,
  });
  const d = data?.data as OrgDetailData | undefined;

  const archive = useMutation({
    mutationFn: () => apiClient.adminArchiveOrg(orgId!, reason || undefined),
    onSuccess: () => {
      toast.success('Org archived');
      qc.invalidateQueries({ queryKey: ['admin', 'org', orgId] });
      qc.invalidateQueries({ queryKey: ['admin', 'orgs'] });
      setConfirming(null);
      setReason('');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const unarchive = useMutation({
    mutationFn: () => apiClient.adminUnarchiveOrg(orgId!),
    onSuccess: () => {
      toast.success('Org unarchived');
      qc.invalidateQueries({ queryKey: ['admin', 'org', orgId] });
      qc.invalidateQueries({ queryKey: ['admin', 'orgs'] });
      setConfirming(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (isLoading || !d) {
    return <div className="p-8 text-center text-muted-foreground">Loading…</div>;
  }

  const o = d.org;
  return (
    <AdminShell detail={o.name} subtitle="You're viewing this org as a platform admin. Members aren't notified.">

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
            {o.kind === 'team'
              ? <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              : <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2 flex-wrap">
              {o.name}
              {o.archivedAt && (
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground inline-flex items-center gap-1">
                  <Archive className="h-3 w-3" /> archived
                </span>
              )}
            </h1>
            <p className="text-sm text-muted-foreground capitalize">{o.kind} org</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {o.archivedAt ? (
            <Button variant="outline" onClick={() => setConfirming('unarchive')} disabled={unarchive.isPending}>
              {unarchive.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Unarchive
            </Button>
          ) : o.kind === 'team' ? (
            <Button variant="outline" onClick={() => setConfirming('archive')} disabled={archive.isPending}
              className="text-red-600 dark:text-red-400 hover:bg-red-500/10">
              <Archive className="h-4 w-4 mr-2" />
              Archive
            </Button>
          ) : null}
        </div>
      </div>

      {confirming === 'archive' && (
        <Card className="p-5 border-red-500/30 bg-red-500/5">
          <p className="text-sm font-semibold mb-2 text-red-700 dark:text-red-300">Archive this org?</p>
          <p className="text-xs text-muted-foreground mb-3">Members lose access. Data is retained — you can unarchive later.</p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional, recorded in audit log)"
            className="w-full text-sm border border-border rounded-md p-2 bg-background mb-3"
            rows={2}
          />
          <div className="flex gap-2">
            <Button onClick={() => archive.mutate()} disabled={archive.isPending} className="bg-red-600 hover:bg-red-700 text-white">
              {archive.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Confirm archive
            </Button>
            <Button variant="outline" onClick={() => { setConfirming(null); setReason(''); }}>Cancel</Button>
          </div>
        </Card>
      )}

      {confirming === 'unarchive' && (
        <Card className="p-5">
          <p className="text-sm font-semibold mb-2">Unarchive this org?</p>
          <p className="text-xs text-muted-foreground mb-3">Members regain access immediately.</p>
          <div className="flex gap-2">
            <Button onClick={() => unarchive.mutate()} disabled={unarchive.isPending}>Confirm unarchive</Button>
            <Button variant="outline" onClick={() => setConfirming(null)}>Cancel</Button>
          </div>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatBox label="Members" value={d.members.length} />
        <StatBox label="Portfolios" value={d.portfolios.length} />
        <StatBox label="Projects" value={d.projects.length} />
        <StatBox label="Findings (open)" value={Object.values(d.findingStats).reduce((a, b) => a + b, 0)} />
      </div>

      {/* Members */}
      <Card className="p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-border">
          <p className="text-sm font-medium uppercase tracking-wide">Members ({d.members.length})</p>
        </div>
        <div className="divide-y divide-border">
          {d.members.map((m) => (
            <Link key={m.userId} to={`/admin/users/${m.userId}`} className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-muted/40">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate flex items-center gap-2">
                  {m.displayName ?? m.email ?? m.userId}
                  {m.suspendedAt && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-500/15 text-red-600">suspended</span>}
                </p>
                <p className="text-xs text-muted-foreground truncate">{m.email}</p>
              </div>
              <span className="text-xs text-muted-foreground capitalize shrink-0">{m.role}</span>
            </Link>
          ))}
        </div>
      </Card>

      {/* Projects */}
      <Card className="p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-border">
          <p className="text-sm font-medium uppercase tracking-wide">Projects ({d.projects.length})</p>
        </div>
        <div className="divide-y divide-border">
          {d.projects.map((p) => (
            <div key={p.projectId} className="px-5 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{p.name}</p>
                <p className="text-xs text-muted-foreground truncate">{p.targetUrl ?? '—'}</p>
              </div>
              <div className="text-xs text-muted-foreground shrink-0 flex items-center gap-3">
                {p.healthScore !== null && (
                  <span className={cn('font-mono tabular-nums', p.healthScore >= 80 ? 'text-green-600' : p.healthScore >= 50 ? 'text-yellow-600' : 'text-red-600')}>
                    {p.healthScore}/100
                  </span>
                )}
                <span>{p.findingCounts?.total ?? 0} findings</span>
              </div>
            </div>
          ))}
          {d.projects.length === 0 && (
            <p className="px-5 py-6 text-sm text-muted-foreground text-center">No projects yet.</p>
          )}
        </div>
      </Card>

      {d.openInvites.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm font-medium uppercase tracking-wide">Open invitations ({d.openInvites.length})</p>
          </div>
          <div className="divide-y divide-border">
            {d.openInvites.map((inv) => (
              <div key={inv.inviteId} className="px-5 py-3 flex items-center justify-between gap-3">
                <p className="text-sm truncate">{inv.email}</p>
                <span className="text-xs text-muted-foreground shrink-0 capitalize">{inv.role}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </AdminShell>
  );
}

function StatBox({ label, value }: { label: string; value: number }) {
  return (
    <Card className="p-4">
      <p className="text-2xl font-bold tabular-nums">{value}</p>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
    </Card>
  );
}
